import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import {
  ngHelper,
  canAddQuantity,
  IProductTypeSize,
  UiContextService,
  IProductTypeListMode,
  getEffectiveMaxQuantity,
} from '@cakeiteasy/web-utils';

import {
  getAddonsString,
  getCartBuildsCount,
  getCartLineByTypeId,
  getCartLinesByTypeId,
  isModalClickIgnored,
  sizeHasMultipleVariants
} from '../../helpers';

import { ICakeCartLine } from '../../../cart';

@Component({
  standalone: false,
  selector: 'single-piece-type',
  templateUrl: './single-piece-type.component.html',
  styleUrls: ['./single-piece-type.component.scss']
})
export class SinglePieceTypeComponent implements OnInit, OnChanges {
  @Input() productType: IProductTypeListMode;
  @Input() cartLines: ICakeCartLine[] = [];
  @Input() showImage: boolean;

  @Output() onSaveToCart = new EventEmitter<{ cartLine: ICakeCartLine; productType: IProductTypeListMode }>();
  @Output() onRemoveCartLine = new EventEmitter<ICakeCartLine>();
  @Output() showModal = new EventEmitter<{
    productType: IProductTypeListMode;
    orderLineBuilderId?: string;
    isEdit?: boolean
  }>();
  @Output() showCart = new EventEmitter<void>();

  public size: IProductTypeSize;
  public minPrice: number;
  public cartLine: ICakeCartLine;
  public buildsCount = 0;

  public itemCount: number;
  public maxCount: number;

  public shouldShowModalOnAdd: boolean;
  public shouldUseCompactLayout: boolean;

  public chosenOptions: string;

  constructor(public uiContext: UiContextService) {
  }

  /** Several builds of this product are in the cart: no counter, the cart actions address them all. */
  public get isMultiBuild(): boolean {
    return this.buildsCount > 1;
  }

  public get isCounterPlusDisabled(): boolean {
    return !canAddQuantity(this.productType, this.itemCount || 0, this.productType.increment || 1);
  }

  ngOnInit() {
    this._init();
  }

  private _setCartLine(cartLines: ICakeCartLine[]) {
    this.cartLine = getCartLineByTypeId(this.productType.id, cartLines);
    this.buildsCount = getCartBuildsCount(getCartLinesByTypeId(this.productType.id, cartLines));
    this.chosenOptions = this.cartLine && getAddonsString(this.cartLine);
    this.itemCount = this.cartLine && this.cartLine.count;
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'cartLines', (cartLines: ICakeCartLine[]) => {
      this._setCartLine(cartLines);
    });
  }

  public onAddToCartClick() {
    if (this.shouldShowModalOnAdd) {
      return this.onShowModal();
    }

    return this.saveToCart();
  }

  public saveToCart() {
    const [product] = this.size.products;
    const count = this.productType.minQuantity || this.productType.increment;
    const cartLine = {
      productId: product.id,
      count,
      bakeryId: this.productType.bakery.id,
    } as ICakeCartLine;

    this.itemCount = count;
    this.onSaveToCart.emit({ cartLine, productType: this.productType });
  }

  public countUpdate(count: number) {
    this.itemCount = count;

    if (!this.cartLine) {
      return;
    }

    if (count === 0) {
      this.onRemoveCartLine.emit(this.cartLine);
    } else {
      this.cartLine.count = count;
      this.onSaveToCart.emit({ cartLine: this.cartLine, productType: this.productType });
    }
  }

  public onShowModal() {
    this.showModal.emit({
      productType: this.productType,
      orderLineBuilderId: this.cartLine?.orderLineBuilderId,
      isEdit: !!this.cartLine,
    });
  }

  public onAddNew() {
    this.showModal.emit({ productType: this.productType, isEdit: false });
  }

  public onShowCart() {
    this.showCart.emit();
  }

  public onProductTypeClick(event: MouseEvent) {
    if (isModalClickIgnored(event)) {
      return;
    }

    // with several builds there is no single one to edit
    if (this.isMultiBuild) {
      return this.onAddNew();
    }

    this.onShowModal();
  }

  private _calculateCompactLayout(): boolean {
    return this.productType
      && this.showImage && !!this.productType.image
      && !!(this.productType.discount && this.productType.discount.value);
  }

  private _init() {
    const [size] = this.productType.sizes;
    this.size = size;
    this.minPrice = this.productType.minPrice || this.size.price;
    this.shouldShowModalOnAdd = sizeHasMultipleVariants(size);
    this.shouldUseCompactLayout = this._calculateCompactLayout();

    this.maxCount = getEffectiveMaxQuantity(this.productType);
  }
}
